import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { 
  Rocket, 
  Target,
  CheckCircle2,
  Clock,
  Sparkles,
  Loader2,
  Calendar,
  Shield,
  Users,
  FileText,
  GraduationCap,
  MessageSquare,
  Building,
  ClipboardCheck,
  Heart,
  AlertTriangle,
  Printer,
  Plus,
  ArrowRight,
  Trash2,
  Link,
  Pencil,
  X,
  Check,
  CalendarDays,
  AlertCircle,
  Brain,
  BookOpen,
  Scale,
  Gavel,
  TrendingUp,
  Lightbulb
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { formatAIResponse } from "@/utils/formatAIContent";

// 90-Day Sprint — acute-care hospital compliance & quality lift.
// Content aligned to CMS Conditions of Participation for Hospitals (42 CFR 482),
// Joint Commission Hospital Accreditation Standards (LD, MS, PC, RI, MM, IC,
// NPSG, EC, EM, LS, HR, PI, RC, IM, PR, TS, UP), Wisconsin DHS Ch 124, and
// the current National Patient Safety Goals. Tasks are written so they're
// concrete enough to check off in one sitting.
const CONTENT_VERSION = "acute_care_wi_v1";

const DEFAULT_SPRINTS = {
  week1: {
    title: "Week 1: Governance & Culture of Safety",
    theme: "CMS 482.12 · Joint Commission LD · WI DHS 124.12",
    focus: "Establish leadership foundation, culture-of-safety baseline, and QAPI structure",
    icon: MessageSquare,
    color: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    areas: [
      {
        name: "Leadership & Governing Body (CMS 482.12 · TJC LD.01)",
        tasks: [
          "Confirm governing body has approved most recent Bylaws and QAPI Plan (past 12 months)",
          "Meet with CEO, CMO, CNO — align on top-3 CMS/TJC risks in the building",
          "Verify governing body meeting minutes reflect QAPI review each meeting",
          "Confirm compliance officer & patient safety officer roles are defined in writing",
          "Review the annual evaluation of the Medical Staff (MS.01.01.01)"
        ]
      },
      {
        name: "Medical Staff Bylaws & Credentialing (CMS 482.22 · TJC MS)",
        tasks: [
          "Audit 5 random credentials files for OPPE/FPPE completeness within cycle",
          "Verify Medical Staff Bylaws current, board-approved, aligned to MS.01.01.01",
          "Confirm every LIP has current DEA (if applicable), NPDB query, and primary source verification",
          "Review process for temporary/emergency privileges — timelines documented",
          "Verify H&P documented within 24 hrs of admission or update prior to procedure"
        ]
      },
      {
        name: "Culture of Safety Baseline (TJC LD.03.01.01)",
        tasks: [
          "Pull AHRQ Hospital Survey on Patient Safety Culture results (or plan to launch)",
          "Identify the two lowest-scoring dimensions — draft owner + due date",
          "Review non-punitive event reporting policy — does it match actual practice?",
          "Meet with front-line staff on 3 units — ask 'What would you fix first?'",
          "Confirm executive leadership rounds on patient safety are scheduled monthly"
        ]
      }
    ]
  },
  week2: {
    title: "Week 2: Patient Rights, Care & NPSGs",
    theme: "CMS 482.13 · TJC RI · PC · NPSGs 2026",
    focus: "Verify patient rights, informed consent, and every 2026 National Patient Safety Goal",
    icon: Shield,
    color: "bg-red-500/20 text-red-400 border-red-500/30",
    areas: [
      {
        name: "Patient Rights & Consent (CMS 482.13 · TJC RI)",
        tasks: [
          "Trace 3 medical records — informed consent complete BEFORE procedure",
          "Verify Advance Directives inquiry documented at admission (RI.01.05.01)",
          "Confirm Patient Rights posted in a language patients can read on every unit",
          "Review grievance process — 7-day written response evidence in last 10 grievances",
          "Verify interpreter services documented for LEP patients — no family members interpreting"
        ]
      },
      {
        name: "Restraint & Seclusion (CMS 482.13(e-f))",
        tasks: [
          "Pull last 5 restraint episodes — order within 1 hr for violent/self-destructive?",
          "Verify face-to-face evaluation within 1 hr by MD/APP or trained RN (violent restraint)",
          "Confirm continuous monitoring documented per hospital policy",
          "Audit debriefing completed within 24 hrs of episode",
          "Verify staff restraint training current for all direct-care RNs"
        ]
      },
      {
        name: "2026 National Patient Safety Goals (TJC NPSG)",
        tasks: [
          "NPSG.01 — Two-patient-identifier tracer on 3 admissions & 3 med passes",
          "NPSG.03 — Look-alike / sound-alike med list posted where meds prepared",
          "NPSG.07 — Hand hygiene compliance ≥ 90% on last 3 months of audits",
          "NPSG.15 — Suicide risk screening completed for all behavioral-health patients (Columbia or equivalent)",
          "NPSG.14 — Central line insertion checklist used every insertion, 100% audit last month"
        ]
      }
    ]
  },
  week3: {
    title: "Week 3: Medication Safety & Infection Prevention",
    theme: "CMS 482.25 · TJC MM · IC · WI DHS 124.14",
    focus: "Close medication-management gaps and drive HAI prevention",
    icon: ClipboardCheck,
    color: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    areas: [
      {
        name: "Medication Management (CMS 482.25 · TJC MM)",
        tasks: [
          "Med reconciliation audit — 10 admissions, 10 discharges (NPSG.03.06.01)",
          "Verify pharmacist review of every order before first dose (MM.05.01.01) — exceptions documented",
          "Audit 5 controlled-substance destructions — two-signature evidence",
          "Verify High-Alert med list (heparin, insulin, opioids, KCl) posted + safeguards active",
          "Confirm ADC (Pyxis/Omnicell) override rate < 5% or trend improving"
        ]
      },
      {
        name: "Infection Prevention & HAIs (CMS 482.42 · TJC IC · NHSN)",
        tasks: [
          "Confirm NHSN CAUTI, CLABSI, C. diff, MRSA bacteremia, SSI submissions current",
          "Verify Antimicrobial Stewardship Program meets CMS COP — 7 core elements documented",
          "Audit hand-hygiene direct observation on every inpatient unit last month",
          "Verify TB risk assessment for the facility completed this fiscal year",
          "Confirm respiratory-etiquette signage + masks at every public entry"
        ]
      },
      {
        name: "Sepsis Bundle & SEP-1 (CMS IQR)",
        tasks: [
          "Pull last 10 SEP-1 cases — 3-hour bundle compliance rate",
          "Verify sepsis screening tool in ED triage — all patients screened",
          "Confirm lactate re-draw within 6 hrs when initial > 2 mmol/L",
          "Meet with ED medical director — sepsis alert workflow ownership",
          "Set sepsis bundle target for this sprint (e.g. 85% compliance)"
        ]
      }
    ]
  },
  week4: {
    title: "Week 4: Environment of Care, EM & Life Safety",
    theme: "CMS 482.41 · TJC EC · EM · LS · WI DHS 124.16",
    focus: "Verify environment, emergency management, and life-safety systems",
    icon: Heart,
    color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    areas: [
      {
        name: "Environment of Care (TJC EC · CMS 482.41)",
        tasks: [
          "Utility-systems management plan reviewed within past 12 months — EC.02.05.01",
          "Verify eyewash stations inspected weekly with documentation",
          "Confirm medical-gas certification current, alarms tested monthly",
          "Review last 4 EC Committee meeting minutes — action items closed",
          "Walk one unit for EC tracer — clutter, blocked egress, unsecured chemicals"
        ]
      },
      {
        name: "Emergency Management (CMS 482.15 · TJC EM)",
        tasks: [
          "Verify Emergency Operations Plan reviewed & board-approved within 12 months",
          "Confirm 2 EM exercises completed in past 12 months (one full-scale)",
          "Verify HVA (Hazard Vulnerability Analysis) updated this fiscal year",
          "Confirm generator load-tested per NFPA 110 monthly + annually",
          "Review Continuity of Operations for water loss, communication loss, IT outage"
        ]
      },
      {
        name: "Life Safety Code (CMS 482.41(b) · TJC LS · NFPA 101)",
        tasks: [
          "Verify eSOC (Statement of Conditions) up to date in TJC portal",
          "Confirm fire drills — 1 per shift per quarter each building (LS.01.01.01)",
          "Verify penetrations above ceiling fire-stopped on last inspection",
          "Confirm sprinkler flow test, fire pump test, and fire-alarm testing current",
          "Review interim life-safety measures (ILSM) policy triggers for construction"
        ]
      }
    ]
  },
  month2: {
    title: "Month 2 (Days 30-60): Performance Improvement & Documentation",
    theme: "CMS 482.21 · TJC PI · IM · RC",
    focus: "Drive QAPI, close documentation gaps, prep survey binders",
    icon: GraduationCap,
    color: "bg-violet-500/20 text-violet-400 border-violet-500/30",
    areas: [
      {
        name: "QAPI Program (CMS 482.21 · TJC PI)",
        tasks: [
          "Confirm at least 4 hospital-wide PI projects active with data, aims, and owners",
          "Verify PI data reported to governing body at every meeting",
          "Pull last 3 RCAs — corrective actions have measurable outcomes",
          "Establish a monthly QAPI dashboard: HAIs, falls, meds, restraints, sepsis",
          "Identify one high-leverage PI project you will personally sponsor"
        ]
      },
      {
        name: "Medical Records & Documentation (CMS 482.24 · TJC RC · IM)",
        tasks: [
          "Chart-completion delinquency rate — CMS threshold no > 50% at any time",
          "Verify H&P update note within 24 hrs of procedure/admission if H&P > 24 hrs old",
          "Audit 10 discharge summaries — completed within 30 days",
          "Verify verbal orders read-back-verified, signed within timeframe per policy",
          "Confirm ePHI access audits performed monthly (HIPAA + TJC IM.02.01.03)"
        ]
      },
      {
        name: "Wisconsin DHS Ch 124 & State Reporting",
        tasks: [
          "Verify all reportable adverse events submitted to WI DHS Division of Quality Assurance",
          "Confirm Wisconsin ADR (Adverse Drug Reaction) reporting workflow",
          "Verify licensure current — hospital, lab (CLIA), pharmacy, imaging",
          "Confirm annual DHS 124 self-assessment or comparable done",
          "Meet with WHA (Wisconsin Hospital Association) rep on peer benchmarks"
        ]
      }
    ]
  },
  month3: {
    title: "Month 3 (Days 60-90): Mock Survey & Sustainment",
    theme: "Full Tracer Methodology · POC Preparation · Board Report",
    focus: "Simulate survey, close findings, build sustainment plan",
    icon: Target,
    color: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
    areas: [
      {
        name: "Mock Survey (Days 60-75)",
        tasks: [
          "Schedule 2-day internal mock survey with cross-department team",
          "Run 6 individual tracers (2 med, 1 surgical, 1 ED, 1 OB or peds, 1 behavioral)",
          "Run 3 system tracers: infection control, medication management, data use",
          "Interview 15 staff members on NPSGs — target > 90% correct responses",
          "Document every finding with severity, standard citation, and owner"
        ]
      },
      {
        name: "Corrective Action & POC (Days 75-85)",
        tasks: [
          "For each finding, write a POC in TJC-accepted format (What/Who/When/How measured)",
          "Assign every finding to a single accountable owner with 30-day close target",
          "Convert top 5 findings into hospital-wide PI projects with monthly review",
          "Verify education completed & documented for anyone involved in a finding",
          "Re-audit each closed finding at 30 days to confirm sustainment"
        ]
      },
      {
        name: "Sustainment & Board Report (Days 85-90)",
        tasks: [
          "Deliver 90-day report to governing body — before-and-after data on 5 metrics",
          "Publish quarterly QAPI dashboard for every unit + executive scorecard",
          "Set standing survey-readiness huddle (weekly) with cross-functional leads",
          "Define next 90-day sprint theme based on residual highest-risk findings",
          "Celebrate & recognize the 3 units with the strongest improvement"
        ]
      }
    ]
  }
};

export default function Sprints() {
  const [sprints, setSprints] = useState(DEFAULT_SPRINTS);
  const [completedTasks, setCompletedTasks] = useState({});
  const [customItems, setCustomItems] = useState([]);
  const [activeWeek, setActiveWeek] = useState("week1");
  const [aiLoading, setAiLoading] = useState(false);
  const [customNote, setCustomNote] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Add item dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newItemText, setNewItemText] = useState("");
  const [newItemArea, setNewItemArea] = useState("");
  const [sendToTasks, setSendToTasks] = useState(true);
  const [taskPriority, setTaskPriority] = useState("ThisWeek");
  const [addingItem, setAddingItem] = useState(false);
  
  // Edit item state
  const [editingItemId, setEditingItemId] = useState(null);
  const [editItemText, setEditItemText] = useState("");
  const [editItemArea, setEditItemArea] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Print mode state
  const [printMode, setPrintMode] = useState(false);
  
  // Sprint start date (for tracking days remaining)
  const [sprintStartDate, setSprintStartDate] = useState(null);
  const [showStartDateDialog, setShowStartDateDialog] = useState(false);
  
  // Intelligence tab state
  const [showIntelligence, setShowIntelligence] = useState(false);
  const [intelligenceCategory, setIntelligenceCategory] = useState("regulatory_compliance");
  const [currentChallenges, setCurrentChallenges] = useState("");
  const [recentFindings, setRecentFindings] = useState("");
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  const [jcStandards, setJcStandards] = useState(null);
  const [surveyFocusAreas, setSurveyFocusAreas] = useState([]);

  // Fetch JC standards and survey focus
  const fetchIntelligenceData = async () => {
    try {
      const [standardsRes, focusRes] = await Promise.all([
        axios.get(`${API}/sprint-intelligence/jc-standards`, { withCredentials: true }),
        axios.get(`${API}/sprint-intelligence/survey-focus`, { withCredentials: true })
      ]);
      setJcStandards(standardsRes.data);
      setSurveyFocusAreas(focusRes.data || []);
    } catch (error) {
      console.error("Error fetching intelligence data:", error);
    }
  };

  // Generate AI sprint suggestions
  const generateSuggestions = async () => {
    setGeneratingSuggestions(true);
    try {
      const response = await axios.post(`${API}/sprint-intelligence/suggest`, {
        category: intelligenceCategory,
        sprint_phase: activeWeek,
        current_challenges: currentChallenges || null,
        recent_findings: recentFindings || null
      }, { withCredentials: true });
      
      setAiSuggestions(response.data);
      toast.success("AI suggestions generated!");
    } catch (error) {
      console.error("Error generating suggestions:", error);
      toast.error("Failed to generate suggestions");
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  // Calculate which week we're in and days remaining
  const getSprintStatus = () => {
    if (!sprintStartDate) return null;
    
    const start = new Date(sprintStartDate);
    const today = new Date();
    const daysSinceStart = Math.floor((today - start) / (1000 * 60 * 60 * 24));
    
    // Determine current week/month
    let currentWeek = "week1";
    let daysRemaining = 7 - daysSinceStart;
    let weekLabel = "Week 1";
    
    if (daysSinceStart < 7) {
      currentWeek = "week1";
      daysRemaining = 7 - daysSinceStart;
      weekLabel = "Week 1";
    } else if (daysSinceStart < 14) {
      currentWeek = "week2";
      daysRemaining = 14 - daysSinceStart;
      weekLabel = "Week 2";
    } else if (daysSinceStart < 21) {
      currentWeek = "week3";
      daysRemaining = 21 - daysSinceStart;
      weekLabel = "Week 3";
    } else if (daysSinceStart < 30) {
      currentWeek = "week4";
      daysRemaining = 30 - daysSinceStart;
      weekLabel = "Week 4";
    } else if (daysSinceStart < 60) {
      currentWeek = "month2";
      daysRemaining = 60 - daysSinceStart;
      weekLabel = "Month 2";
    } else if (daysSinceStart < 90) {
      currentWeek = "month3";
      daysRemaining = 90 - daysSinceStart;
      weekLabel = "Month 3";
    } else {
      currentWeek = "month3";
      daysRemaining = 0;
      weekLabel = "Completed!";
    }
    
    return {
      currentWeek,
      daysRemaining,
      daysSinceStart,
      weekLabel,
      isOnTrack: getSprintProgress(currentWeek) >= (daysSinceStart % 7) / 7 * 100
    };
  };

  const saveSprintStartDate = (date) => {
    setSprintStartDate(date);
    localStorage.setItem("sprint_start_date", date);
    setShowStartDateDialog(false);
    
    // Auto-switch to current week
    const status = getSprintStatus();
    if (status) {
      setActiveWeek(status.currentWeek);
    }
    toast.success("Sprint started! Good luck!");
  };

  // Fetch sprint progress and custom items from backend
  const fetchData = useCallback(async () => {
    try {
      const [progressRes, itemsRes] = await Promise.all([
        axios.get(`${API}/sprint-progress`, { withCredentials: true }),
        axios.get(`${API}/sprint-items`, { withCredentials: true })
      ]);
      
      if (progressRes.data?.completed_tasks) {
        setCompletedTasks(progressRes.data.completed_tasks);
      }
      setCustomItems(itemsRes.data || []);
    } catch (error) {
      console.error("Error fetching sprint data:", error);
      // Fall back to session storage
      const saved = sessionStorage.getItem("sprint_progress");
      if (saved) {
        setCompletedTasks(JSON.parse(saved));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Load sprint start date
    const savedStartDate = localStorage.getItem("sprint_start_date");
    if (savedStartDate) {
      setSprintStartDate(savedStartDate);
    }
    // If the sprint content changes (new template), old task keys become
    // meaningless — wipe stale completions once, then remember we're on the
    // new version so we never wipe again.
    const seenVersion = localStorage.getItem("sprint_content_version");
    if (seenVersion !== CONTENT_VERSION) {
      localStorage.setItem("sprint_content_version", CONTENT_VERSION);
      // Fire-and-forget: server-side wipe of stale progress keys
      axios.post(`${API}/sprint-progress`, { completed_tasks: {} },
        { withCredentials: true }).catch(() => { /* non-fatal */ });
      setCompletedTasks({});
    }
  }, [fetchData]);

  // One-click restart: sets start date to tomorrow, clears all completions
  // (server + local), and re-fetches so the UI reflects a clean sprint.
  const resetAndStartTomorrow = async () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isoTomorrow = tomorrow.toISOString().split('T')[0];
    try {
      await axios.post(`${API}/sprint-progress`, { completed_tasks: {} },
        { withCredentials: true });
    } catch (_) { /* non-fatal */ }
    sessionStorage.removeItem("sprint_progress");
    localStorage.setItem("sprint_start_date", isoTomorrow);
    setSprintStartDate(isoTomorrow);
    setCompletedTasks({});
    setActiveWeek("week1");
    toast.success(`Sprint reset — starts tomorrow (${isoTomorrow}). Week 1 loaded.`);
  };

  // Save progress to backend when it changes
  const saveProgress = useCallback(async (newCompletedTasks) => {
    try {
      await axios.post(`${API}/sprint-progress`, {
        completed_tasks: newCompletedTasks
      }, { withCredentials: true });
    } catch (error) {
      console.error("Error saving progress:", error);
      // Fall back to session storage
      sessionStorage.setItem("sprint_progress", JSON.stringify(newCompletedTasks));
    }
  }, []);

  const toggleTask = (weekKey, areaIndex, taskIndex) => {
    const key = `${weekKey}-${areaIndex}-${taskIndex}`;
    const newCompletedTasks = {
      ...completedTasks,
      [key]: !completedTasks[key]
    };
    setCompletedTasks(newCompletedTasks);
    saveProgress(newCompletedTasks);
  };

  const toggleCustomItem = async (itemId, currentStatus) => {
    try {
      await axios.patch(`${API}/sprint-items/${itemId}`, null, {
        params: { completed: !currentStatus },
        withCredentials: true
      });
      setCustomItems(prev => prev.map(item => 
        item.item_id === itemId ? { ...item, completed: !currentStatus } : item
      ));
      if (!currentStatus) {
        toast.success("Item completed!", { description: "Great progress!" });
      }
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  // Fetch intelligence data when tab is opened (lazy: only first time per session)
  useEffect(() => {
    if (showIntelligence && !jcStandards) {
      fetchIntelligenceData();
    }
  }, [showIntelligence, jcStandards]);

  const deleteCustomItem = async (itemId) => {
    try {
      await axios.delete(`${API}/sprint-items/${itemId}`, { withCredentials: true });
      setCustomItems(prev => prev.filter(item => item.item_id !== itemId));
      toast.success("Item removed");
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const startEditingItem = (item) => {
    setEditingItemId(item.item_id);
    setEditItemText(item.task_text);
    setEditItemArea(item.area_name);
  };

  const cancelEditing = () => {
    setEditingItemId(null);
    setEditItemText("");
    setEditItemArea("");
  };

  const saveEditedItem = async (itemId) => {
    if (!editItemText.trim()) {
      toast.error("Task text cannot be empty");
      return;
    }
    
    setSavingEdit(true);
    try {
      const response = await axios.patch(`${API}/sprint-items/${itemId}`, {
        task_text: editItemText.trim(),
        area_name: editItemArea
      }, { withCredentials: true });
      
      setCustomItems(prev => prev.map(item => 
        item.item_id === itemId 
          ? { ...item, task_text: editItemText.trim(), area_name: editItemArea }
          : item
      ));
      
      setEditingItemId(null);
      setEditItemText("");
      setEditItemArea("");
      
      if (response.data.linked_task_updated) {
        toast.success("Item updated & synced to Tasks!");
      } else {
        toast.success("Item updated!");
      }
    } catch (error) {
      toast.error("Failed to update item");
    } finally {
      setSavingEdit(false);
    }
  };

  const addCustomItem = async () => {
    if (!newItemText.trim() || !newItemArea) {
      toast.error("Please enter task text and select an area");
      return;
    }
    
    setAddingItem(true);
    try {
      const response = await axios.post(`${API}/sprint-items`, {
        sprint_week: activeWeek,
        area_name: newItemArea,
        task_text: newItemText.trim(),
        send_to_tasks: sendToTasks,
        task_priority: taskPriority
      }, { withCredentials: true });
      
      setCustomItems(prev => [...prev, response.data]);
      setAddDialogOpen(false);
      setNewItemText("");
      setNewItemArea("");
      
      if (sendToTasks) {
        toast.success("Item added & sent to Tasks!", {
          description: `Added to ${taskPriority === "Today" ? "Today" : taskPriority === "ThisWeek" ? "This Week" : "Parking Lot"}`
        });
      } else {
        toast.success("Item added to sprint!");
      }
    } catch (error) {
      toast.error("Failed to add item");
    } finally {
      setAddingItem(false);
    }
  };

  const isTaskComplete = (weekKey, areaIndex, taskIndex) => {
    return completedTasks[`${weekKey}-${areaIndex}-${taskIndex}`] || false;
  };

  const getSprintProgress = (weekKey) => {
    const sprint = sprints[weekKey];
    if (!sprint) return 0;
    
    let total = 0;
    let completed = 0;
    
    // Count default tasks
    sprint.areas.forEach((area, areaIndex) => {
      area.tasks.forEach((_, taskIndex) => {
        total++;
        if (isTaskComplete(weekKey, areaIndex, taskIndex)) {
          completed++;
        }
      });
    });
    
    // Count custom items for this week
    const weekCustomItems = customItems.filter(item => item.sprint_week === weekKey);
    total += weekCustomItems.length;
    completed += weekCustomItems.filter(item => item.completed).length;
    
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  // Get custom items for a specific area in the current week
  const getCustomItemsForArea = (weekKey, areaName) => {
    return customItems.filter(item => 
      item.sprint_week === weekKey && item.area_name === areaName
    );
  };

  const getTotalProgress = () => {
    const weekKeys = Object.keys(sprints);
    const progresses = weekKeys.map(k => getSprintProgress(k));
    return Math.round(progresses.reduce((a, b) => a + b, 0) / weekKeys.length);
  };

  const getAISuggestions = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/draft`, {
        context: "Healthcare executive onboarding - Joint Commission certified psychiatric hospital, Medicaid enrolled, units include Detox (200), Adult (300), Adult High Acuity (400), Adolescent (500, 600)",
        problem: `I'm a new executive leader (RN/Infection Preventionist background) starting at Miramont Behavioral Health, an acute psychiatric hospital. 

Current sprint focus: ${sprints[activeWeek]?.title} - ${sprints[activeWeek]?.theme}

${customNote ? `My specific situation/question: ${customNote}` : ''}

Please provide:
1. Additional specific tasks I should consider for this sprint
2. Key questions I should be asking
3. Red flags to watch for
4. Quick wins I might achieve this week
5. Resources or benchmarks to reference`
      }, { withCredentials: true });

      toast.success("AI suggestions ready!");
      // Could add the response to a suggestions panel
      return response.data.draft;
    } catch (error) {
      toast.error("Failed to get AI suggestions");
    } finally {
      setAiLoading(false);
    }
  };

  const handlePrint = () => {
    setPrintMode(true);
    // Give React time to render the print view before opening print dialog
    setTimeout(() => {
      window.print();
      // Keep print mode active a bit longer to ensure print dialog has the right content
      setTimeout(() => {
        setPrintMode(false);
      }, 500);
    }, 200);
  };

  // Print-friendly view helper function
  const renderPrintView = () => {
    const today = new Date().toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    return (
      <div className="sprint-print-container bg-white text-black p-8 min-h-screen">
        {/* Print Header */}
        <div className="sprint-print-header text-center border-b-2 border-gray-800 pb-4 mb-6">
          <h1 className="text-2xl font-bold text-black mb-1">90-Day Acute Care Compliance Sprint</h1>
          <p className="text-sm text-gray-600">CMS · Joint Commission · Wisconsin DHS Ch 124 | {today}</p>
          <p className="text-xs text-gray-500 mt-1">Overall Progress: {getTotalProgress()}%</p>
        </div>
        
        {/* Sprint Sections */}
        {Object.entries(sprints).map(([weekKey, sprint]) => {
          const progress = getSprintProgress(weekKey);
          const weekCustomItems = customItems.filter(item => item.sprint_week === weekKey);
          
          return (
            <div key={weekKey} className="sprint-print-section mb-6 border border-gray-300 p-4 break-inside-avoid">
              {/* Sprint Header */}
              <div className="sprint-print-section-header border-b border-gray-400 pb-2 mb-3">
                <h2 className="text-lg font-bold text-black">{sprint.title}</h2>
                <p className="text-sm italic text-gray-600">{sprint.theme}</p>
                <p className="text-xs text-gray-500">Focus: {sprint.focus} | Progress: {progress}%</p>
              </div>
              
              {/* Areas */}
              {sprint.areas.map((area, areaIndex) => {
                const areaCustomItems = weekCustomItems.filter(item => item.area_name === area.name);
                const defaultCompleted = area.tasks.filter((_, taskIndex) => 
                  isTaskComplete(weekKey, areaIndex, taskIndex)
                ).length;
                const customCompleted = areaCustomItems.filter(item => item.completed).length;
                const totalTasks = area.tasks.length + areaCustomItems.length;
                const totalCompleted = defaultCompleted + customCompleted;
                
                return (
                  <div key={areaIndex} className="sprint-print-area mb-4">
                    <div className="sprint-print-area-title bg-gray-100 px-2 py-1 font-semibold text-sm flex justify-between">
                      <span>{area.name}</span>
                      <span className="text-gray-600">{totalCompleted}/{totalTasks}</span>
                    </div>
                    
                    {/* Default Tasks */}
                    {area.tasks.map((task, taskIndex) => {
                      const isComplete = isTaskComplete(weekKey, areaIndex, taskIndex);
                      return (
                        <div key={taskIndex} className="sprint-print-task flex items-start gap-2 py-1 px-2 text-sm">
                          <div className={`sprint-print-task-checkbox w-3 h-3 border border-gray-800 mt-1 flex-shrink-0 ${isComplete ? 'bg-gray-800' : ''}`} />
                          <span className={`sprint-print-task-text ${isComplete ? 'line-through text-gray-500' : ''}`}>
                            {task}
                          </span>
                        </div>
                      );
                    })}
                    
                    {/* Custom Items for this area */}
                    {areaCustomItems.map((item) => (
                      <div key={item.item_id} className="sprint-print-task flex items-start gap-2 py-1 px-2 text-sm bg-gray-50 border-l-2 border-gray-400">
                        <div className={`sprint-print-task-checkbox w-3 h-3 border border-gray-800 mt-1 flex-shrink-0 ${item.completed ? 'bg-gray-800' : ''}`} />
                        <span className={`sprint-print-task-text ${item.completed ? 'line-through text-gray-500' : ''}`}>
                          {item.task_text} {item.linked_task_id ? '★' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
              
              {/* Custom / Other items */}
              {(() => {
                const customOtherItems = weekCustomItems.filter(item => item.area_name === "Custom");
                if (customOtherItems.length === 0) return null;
                
                return (
                  <div className="sprint-print-area mb-4">
                    <div className="sprint-print-area-title bg-gray-100 px-2 py-1 font-semibold text-sm flex justify-between">
                      <span>Custom Items</span>
                      <span className="text-gray-600">{customOtherItems.filter(i => i.completed).length}/{customOtherItems.length}</span>
                    </div>
                    {customOtherItems.map((item) => (
                      <div key={item.item_id} className="sprint-print-task flex items-start gap-2 py-1 px-2 text-sm bg-gray-50 border-l-2 border-gray-400">
                        <div className={`sprint-print-task-checkbox w-3 h-3 border border-gray-800 mt-1 flex-shrink-0 ${item.completed ? 'bg-gray-800' : ''}`} />
                        <span className={`sprint-print-task-text ${item.completed ? 'line-through text-gray-500' : ''}`}>
                          {item.task_text} {item.linked_task_id ? '★' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          );
        })}
        
        {/* Print Footer */}
        <div className="sprint-print-footer text-center text-xs text-gray-500 border-t border-gray-300 pt-2 mt-4">
          <p>★ = Task linked to Task Manager | 90-Day Sprint Planner</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show print view when printMode is active
  if (printMode) {
    return renderPrintView();
  }

  return (
    <div data-testid="sprints-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Rocket className="w-6 h-6 text-violet-500" />
            Acute Care Compliance Sprint
          </h1>
          <p className="text-muted-foreground">
            90-day roadmap to CMS + Joint Commission survey readiness · Wisconsin acute care
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Sprint Status Badge */}
          {sprintStartDate ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/30">
              <CalendarDays className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-medium text-violet-400">
                {(() => {
                  const status = getSprintStatus();
                  if (!status) return "Day 0";
                  if (status.daysRemaining <= 0) return "90-Day Complete! 🎉";
                  return `Day ${status.daysSinceStart + 1} • ${status.daysRemaining}d left in ${status.weekLabel}`;
                })()}
              </span>
              {(() => {
                const status = getSprintStatus();
                if (status && !status.isOnTrack && status.daysRemaining > 0) {
                  return (
                    <AlertCircle className="w-4 h-4 text-amber-400" title="Behind pace" />
                  );
                }
                return null;
              })()}
              {/* One-click restart — resets start date to tomorrow and clears progress */}
              <Button
                variant="outline"
                size="sm"
                onClick={resetAndStartTomorrow}
                data-testid="reset-sprint-btn"
                className="ml-1 text-xs"
                title="Reset sprint — starts tomorrow, clears progress"
              >
                <Rocket className="w-3 h-3 mr-1" />
                Restart Tomorrow
              </Button>
            </div>
          ) : (
            <Dialog open={showStartDateDialog} onOpenChange={setShowStartDateDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Set Start Date
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>When did you start?</DialogTitle>
                  <DialogDescription>
                    Set your 90-day sprint start date to track progress and see which week you&apos;re in.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      variant="default"
                      className="bg-violet-600 hover:bg-violet-700"
                      onClick={() => {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        saveSprintStartDate(tomorrow.toISOString().split('T')[0]);
                      }}
                      data-testid="start-tomorrow-btn"
                    >
                      Start Tomorrow
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => saveSprintStartDate(new Date().toISOString().split('T')[0])}
                    >
                      Start Today
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const lastWeek = new Date();
                        lastWeek.setDate(lastWeek.getDate() - 7);
                        saveSprintStartDate(lastWeek.toISOString().split('T')[0]);
                      }}
                    >
                      Last Week
                    </Button>
                  </div>
                  <div className="text-center text-sm text-muted-foreground">or</div>
                  <div className="space-y-2">
                    <Label>Custom Start Date</Label>
                    <Input
                      type="date"
                      onChange={(e) => {
                        if (e.target.value) saveSprintStartDate(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          {/* Regulatory Intelligence Button */}
          <Button 
            variant={showIntelligence ? "default" : "outline"}
            onClick={() => setShowIntelligence(!showIntelligence)}
            className={showIntelligence ? "bg-amber-600 hover:bg-amber-700" : ""}
          >
            <Scale className="w-4 h-4 mr-2" />
            {showIntelligence ? "Hide Intelligence" : "JC/CMS Intelligence"}
          </Button>
          
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="capture-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-violet-400" />
                  Add Sprint Item
                </DialogTitle>
                <DialogDescription>
                  Add a custom task to your {sprints[activeWeek]?.title} sprint
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Task Description</Label>
                  <Input
                    placeholder="e.g., Review medication reconciliation process"
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Area/Category</Label>
                  <Select value={newItemArea} onValueChange={setNewItemArea}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an area" />
                    </SelectTrigger>
                    <SelectContent>
                      {sprints[activeWeek]?.areas.map((area, idx) => (
                        <SelectItem key={idx} value={area.name}>
                          {area.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="Custom">Custom / Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-400" />
                    <Label htmlFor="send-to-tasks" className="cursor-pointer">
                      Also add to Task Manager
                    </Label>
                  </div>
                  <Switch
                    id="send-to-tasks"
                    checked={sendToTasks}
                    onCheckedChange={setSendToTasks}
                  />
                </div>
                {sendToTasks && (
                  <div className="space-y-2 pl-4 border-l-2 border-emerald-500/30">
                    <Label>Task Priority</Label>
                    <Select value={taskPriority} onValueChange={setTaskPriority}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Today">Today</SelectItem>
                        <SelectItem value="ThisWeek">This Week</SelectItem>
                        <SelectItem value="ParkingLot">Parking Lot</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={addCustomItem} disabled={addingItem} className="capture-btn">
                  {addingItem ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      {/* Regulatory Intelligence Panel */}
      {showIntelligence && (
        <Card className="bento-card border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Scale className="w-5 h-5 text-amber-400" />
              Joint Commission & CMS Intelligence
              <Badge variant="outline" className="ml-2 text-xs">2026 Standards</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Survey Focus Areas */}
            {surveyFocusAreas.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  2026 Survey Hot Spots
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {surveyFocusAreas.slice(0, 6).map((area) => (
                    <div key={area.chapter} className="p-2 rounded-lg bg-zinc-900/50 border border-zinc-800">
                      <p className="text-xs font-semibold text-amber-400">{area.chapter} - {area.name}</p>
                      <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                        {area.survey_focus_2026.slice(0, 2).map((focus, idx) => (
                          <li key={idx}>• {focus}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Sprint Suggestions */}
            <div className="border-t border-zinc-800 pt-4 space-y-3">
              <Label className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-violet-400" />
                AI-Powered Sprint Suggestions
              </Label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Focus Category</Label>
                  <Select value={intelligenceCategory} onValueChange={setIntelligenceCategory}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="patient_safety">🛡️ Patient Safety & Risk</SelectItem>
                      <SelectItem value="quality_improvement">📈 Quality & Performance</SelectItem>
                      <SelectItem value="regulatory_compliance">⚖️ Regulatory & Survey Prep</SelectItem>
                      <SelectItem value="clinical_excellence">💗 Clinical Excellence</SelectItem>
                      <SelectItem value="workforce">👥 Workforce & Culture</SelectItem>
                      <SelectItem value="environment">🏥 Environment of Care</SelectItem>
                      <SelectItem value="patient_experience">😊 Patient Experience</SelectItem>
                      <SelectItem value="strategic">🎯 Strategic Initiatives</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Current Challenges (optional)</Label>
                  <Input
                    placeholder="e.g., High restraint use, staffing gaps..."
                    value={currentChallenges}
                    onChange={(e) => setCurrentChallenges(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Recent Findings/Gaps (optional)</Label>
                <Textarea
                  placeholder="e.g., Mock survey found documentation gaps in suicide risk assessments..."
                  value={recentFindings}
                  onChange={(e) => setRecentFindings(e.target.value)}
                  className="min-h-[60px]"
                />
              </div>

              <Button 
                onClick={generateSuggestions}
                disabled={generatingSuggestions}
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                {generatingSuggestions ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing Standards & Generating...
                  </>
                ) : (
                  <>
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Generate AI Sprint Suggestions
                  </>
                )}
              </Button>

              {/* AI Suggestions Results */}
              {aiSuggestions && (
                <div className="mt-4 p-4 rounded-lg bg-zinc-900 border border-amber-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-semibold">AI Recommendations</span>
                    <Badge variant="outline" className="text-xs">{aiSuggestions.category}</Badge>
                  </div>
                  <ScrollArea className="h-[300px]">
                    <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap text-sm">
                      {formatAIResponse(aiSuggestions.suggestions)}
                    </div>
                  </ScrollArea>
                  <p className="text-xs text-muted-foreground mt-3 border-t border-zinc-800 pt-2">
                    💡 Tip: Copy specific tasks above and add them to your sprint using the &quot;Add Item&quot; button
                  </p>
                </div>
              )}
            </div>

            {/* Quick Reference */}
            <div className="border-t border-zinc-800 pt-4">
              <Accordion type="single" collapsible className="space-y-2">
                <AccordionItem value="jc-chapters" className="border-zinc-800">
                  <AccordionTrigger className="text-sm hover:no-underline">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-blue-400" />
                      Joint Commission BHC Chapters Reference
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pt-2">
                      {jcStandards && Object.entries(jcStandards).map(([code, std]) => (
                        <div key={code} className="p-2 rounded bg-zinc-900/50 border border-zinc-800 text-xs">
                          <p className="font-semibold text-blue-400">{code}: {std.name}</p>
                          <p className="text-muted-foreground mt-1 line-clamp-2">{std.description}</p>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="wisconsin" className="border-zinc-800">
                  <AccordionTrigger className="text-sm hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Gavel className="w-4 h-4 text-emerald-400" />
                      Wisconsin Behavioral Health Regulations
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-2 text-xs">
                      <div className="p-2 rounded bg-zinc-900/50 border border-zinc-800">
                        <p className="font-semibold">Chapter 51 - Mental Health Act</p>
                        <p className="text-muted-foreground">Voluntary/involuntary admission, patient rights, emergency detention, commitment procedures</p>
                      </div>
                      <div className="p-2 rounded bg-zinc-900/50 border border-zinc-800">
                        <p className="font-semibold">DHS 124 - Hospital Licensing</p>
                        <p className="text-muted-foreground">Hospital licensing requirements, quality standards, patient rights</p>
                      </div>
                      <div className="p-2 rounded bg-zinc-900/50 border border-zinc-800">
                        <p className="font-semibold">Chapter 55 - Protective Services</p>
                        <p className="text-muted-foreground">Protective placement, guardianship, vulnerable adult protection</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overall Progress */}
      <Card className="bento-card">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Overall 90-Day Progress</span>
            <span className="text-2xl font-bold text-primary">{getTotalProgress()}%</span>
          </div>
          <Progress value={getTotalProgress()} className="h-3" />
          <div className="hidden sm:flex justify-between mt-2 text-xs text-muted-foreground">
            <span>Week 1</span>
            <span>Week 2</span>
            <span>Week 3</span>
            <span>Week 4</span>
            <span>Month 2</span>
            <span>Month 3</span>
          </div>
          <div className="flex sm:hidden justify-between mt-2 text-[10px] text-muted-foreground">
            <span>W1</span>
            <span>W2</span>
            <span>W3</span>
            <span>W4</span>
            <span>M2</span>
            <span>M3</span>
          </div>
        </CardContent>
      </Card>

      {/* Sprint Tabs */}
      <Tabs value={activeWeek} onValueChange={setActiveWeek}>
        <TabsList className="bg-zinc-900 p-1 h-auto flex-wrap gap-1 w-full justify-start">
          {Object.entries(sprints).map(([key, sprint]) => {
            const progress = getSprintProgress(key);
            const SprintIcon = sprint.icon;
            return (
              <TabsTrigger 
                key={key}
                value={key}
                className="flex items-center gap-2 px-3 py-2 data-[state=active]:bg-primary/20 relative"
                data-testid={`sprint-tab-${key}`}
              >
                <SprintIcon className="w-4 h-4" />
                <span className="hidden sm:inline">{sprint.title.split(':')[0]}</span>
                {progress === 100 && (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 absolute -top-1 -right-1" />
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(sprints).map(([weekKey, sprint]) => {
          const SprintIcon = sprint.icon;
          const progress = getSprintProgress(weekKey);
          
          return (
            <TabsContent key={weekKey} value={weekKey} className="mt-4">
              {/* Sprint Header */}
              <Card className={`bento-card border ${sprint.color.replace('bg-', 'border-').replace('/20', '/30')}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg ${sprint.color.split(' ')[0]} flex items-center justify-center`}>
                        <SprintIcon className={`w-6 h-6 ${sprint.color.split(' ')[1]}`} />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{sprint.title}</CardTitle>
                        <p className="text-sm text-muted-foreground">{sprint.theme}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold">{progress}%</div>
                      <p className="text-xs text-muted-foreground">complete</p>
                    </div>
                  </div>
                  <p className="text-sm mt-2 p-2 bg-zinc-800/50 rounded-lg">
                    <strong>Focus:</strong> {sprint.focus}
                  </p>
                </CardHeader>
              </Card>

              {/* Sprint Areas */}
              <div className="mt-4 space-y-4">
                <Accordion type="multiple" defaultValue={[...sprint.areas.map((_, i) => `area-${i}`), 'custom-area']} className="space-y-2">
                  {sprint.areas.map((area, areaIndex) => {
                    const areaCustomItems = getCustomItemsForArea(weekKey, area.name);
                    const totalTasks = area.tasks.length + areaCustomItems.length;
                    const defaultCompleted = area.tasks.filter((_, taskIndex) => 
                      isTaskComplete(weekKey, areaIndex, taskIndex)
                    ).length;
                    const customCompleted = areaCustomItems.filter(item => item.completed).length;
                    const totalCompleted = defaultCompleted + customCompleted;
                    
                    return (
                      <AccordionItem 
                        key={areaIndex} 
                        value={`area-${areaIndex}`}
                        className="bento-card border-none"
                      >
                        <AccordionTrigger className="px-4 hover:no-underline">
                          <div className="flex items-center justify-between w-full pr-4">
                            <span className="font-medium">{area.name}</span>
                            <Badge variant="outline" className={totalCompleted === totalTasks ? "bg-emerald-500/20 text-emerald-400" : ""}>
                              {totalCompleted}/{totalTasks}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="space-y-2">
                            {/* Default tasks */}
                            {area.tasks.map((task, taskIndex) => {
                              const isComplete = isTaskComplete(weekKey, areaIndex, taskIndex);
                              return (
                                <div 
                                  key={taskIndex}
                                  className={`flex items-start gap-3 p-2 rounded-lg transition-colors ${
                                    isComplete ? 'bg-emerald-500/10' : 'bg-zinc-800/30 hover:bg-zinc-800/50'
                                  }`}
                                >
                                  <Checkbox
                                    checked={isComplete}
                                    onCheckedChange={() => toggleTask(weekKey, areaIndex, taskIndex)}
                                    className="mt-0.5"
                                  />
                                  <span className={`text-sm ${isComplete ? 'line-through text-muted-foreground' : ''}`}>
                                    {task}
                                  </span>
                                </div>
                              );
                            })}
                            {/* Custom items for this area */}
                            {areaCustomItems.map((item) => (
                              <div 
                                key={item.item_id}
                                className={`flex items-start gap-3 p-2 rounded-lg transition-colors border border-violet-500/30 ${
                                  item.completed ? 'bg-emerald-500/10' : 'bg-violet-500/5 hover:bg-violet-500/10'
                                }`}
                              >
                                {editingItemId === item.item_id ? (
                                  /* Edit mode */
                                  <>
                                    <div className="flex-1 space-y-2">
                                      <Input
                                        value={editItemText}
                                        onChange={(e) => setEditItemText(e.target.value)}
                                        className="text-sm"
                                        autoFocus
                                      />
                                      <Select value={editItemArea} onValueChange={setEditItemArea}>
                                        <SelectTrigger className="text-sm">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {sprints[weekKey]?.areas.map((a, idx) => (
                                            <SelectItem key={idx} value={a.name}>
                                              {a.name}
                                            </SelectItem>
                                          ))}
                                          <SelectItem value="Custom">Custom / Other</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
                                        onClick={() => saveEditedItem(item.item_id)}
                                        disabled={savingEdit}
                                      >
                                        {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-muted-foreground hover:text-white"
                                        onClick={cancelEditing}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </>
                                ) : (
                                  /* View mode */
                                  <>
                                    <Checkbox
                                      checked={item.completed}
                                      onCheckedChange={() => toggleCustomItem(item.item_id, item.completed)}
                                      className="mt-0.5"
                                    />
                                    <div className="flex-1">
                                      <span className={`text-sm ${item.completed ? 'line-through text-muted-foreground' : ''}`}>
                                        {item.task_text}
                                      </span>
                                      {item.linked_task_id && (
                                        <div className="flex items-center gap-1 mt-1">
                                          <Link className="w-3 h-3 text-emerald-400" />
                                          <span className="text-xs text-emerald-400">Linked to Tasks</span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-violet-400"
                                        onClick={() => startEditingItem(item)}
                                      >
                                        <Pencil className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-red-400"
                                        onClick={() => deleteCustomItem(item.item_id)}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                  
                  {/* Custom / Other area for items that don't fit default areas */}
                  {(() => {
                    const customOtherItems = customItems.filter(item => 
                      item.sprint_week === weekKey && item.area_name === "Custom"
                    );
                    if (customOtherItems.length === 0) return null;
                    
                    const customCompleted = customOtherItems.filter(item => item.completed).length;
                    
                    return (
                      <AccordionItem 
                        value="custom-area"
                        className="bento-card border-none border-l-2 border-l-violet-500/50"
                      >
                        <AccordionTrigger className="px-4 hover:no-underline">
                          <div className="flex items-center justify-between w-full pr-4">
                            <span className="font-medium flex items-center gap-2">
                              <Plus className="w-4 h-4 text-violet-400" />
                              Custom Items
                            </span>
                            <Badge variant="outline" className={customCompleted === customOtherItems.length ? "bg-emerald-500/20 text-emerald-400" : "border-violet-500/50"}>
                              {customCompleted}/{customOtherItems.length}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="space-y-2">
                            {customOtherItems.map((item) => (
                              <div 
                                key={item.item_id}
                                className={`flex items-start gap-3 p-2 rounded-lg transition-colors border border-violet-500/30 ${
                                  item.completed ? 'bg-emerald-500/10' : 'bg-violet-500/5 hover:bg-violet-500/10'
                                }`}
                              >
                                {editingItemId === item.item_id ? (
                                  /* Edit mode */
                                  <>
                                    <div className="flex-1 space-y-2">
                                      <Input
                                        value={editItemText}
                                        onChange={(e) => setEditItemText(e.target.value)}
                                        className="text-sm"
                                        autoFocus
                                      />
                                      <Select value={editItemArea} onValueChange={setEditItemArea}>
                                        <SelectTrigger className="text-sm">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {sprints[weekKey]?.areas.map((a, idx) => (
                                            <SelectItem key={idx} value={a.name}>
                                              {a.name}
                                            </SelectItem>
                                          ))}
                                          <SelectItem value="Custom">Custom / Other</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20"
                                        onClick={() => saveEditedItem(item.item_id)}
                                        disabled={savingEdit}
                                      >
                                        {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-muted-foreground hover:text-white"
                                        onClick={cancelEditing}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </>
                                ) : (
                                  /* View mode */
                                  <>
                                    <Checkbox
                                      checked={item.completed}
                                      onCheckedChange={() => toggleCustomItem(item.item_id, item.completed)}
                                      className="mt-0.5"
                                    />
                                    <div className="flex-1">
                                      <span className={`text-sm ${item.completed ? 'line-through text-muted-foreground' : ''}`}>
                                        {item.task_text}
                                      </span>
                                      {item.linked_task_id && (
                                        <div className="flex items-center gap-1 mt-1">
                                          <Link className="w-3 h-3 text-emerald-400" />
                                          <span className="text-xs text-emerald-400">Linked to Tasks</span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-violet-400"
                                        onClick={() => startEditingItem(item)}
                                      >
                                        <Pencil className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-red-400"
                                        onClick={() => deleteCustomItem(item.item_id)}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })()}
                </Accordion>
              </div>

              {/* AI Suggestions */}
              <Card className="bento-card mt-4 border-violet-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-400" />
                    AI Sprint Coach
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Input
                    value={customNote}
                    onChange={(e) => setCustomNote(e.target.value)}
                    placeholder="Add context about your specific situation (optional)"
                    className="text-sm"
                  />
                  <Button 
                    onClick={getAISuggestions} 
                    disabled={aiLoading}
                    className="w-full capture-btn"
                  >
                    {aiLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating suggestions...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Get AI Suggestions for This Sprint
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    AI will suggest additional tasks, questions, and quick wins based on your facility type
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Facility Context Reminder */}
      <Card className="bento-card bg-zinc-800/30">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building className="w-4 h-4" />
            <span>Miramont Behavioral Health</span>
            <Badge variant="outline" className="text-xs">Joint Commission</Badge>
            <Badge variant="outline" className="text-xs">Medicaid</Badge>
            <Badge variant="outline" className="text-xs">Units: 200, 300, 400, 500, 600</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
